import { useState, useCallback, useMemo } from "react";
import {
  type TokenWithMonitoring,
  useTokensByMonitoringType,
} from "@/features/auth/hooks/useApiToken";
import { useTokenUsageTracking } from "./useTokenUsageTracking";
import { useTokenValidation } from "./useTokenValidation";
import { useApiTokenToast } from "./useApiTokenToast";
import { type MonitoringType } from "@/constants/monitoring";

interface UseTokenManagementProps {
  apiToken: string | null;
  isApiTokenLoading?: boolean;
  user?: any;
  showTokenDropdown?: boolean;
  additionalValidation?: () => boolean;
  monitoringType?: MonitoringType;
}

/**
 * Unified hook for token management across containers
 * Combines token validation, selection, usage tracking, and toast notifications
 */
export const useTokenManagement = ({
  apiToken,
  isApiTokenLoading,
  user,
  showTokenDropdown = true,
  additionalValidation,
  monitoringType,
}: UseTokenManagementProps) => {
  const [selectedToken, setSelectedToken] =
    useState<TokenWithMonitoring | null>(null);

  const { trackTokenUsage } = useTokenUsageTracking();
  useApiTokenToast(apiToken ?? "", user, isApiTokenLoading);

  const { tokens: availableTokens, isPending: tokensLoading } =
    useTokensByMonitoringType(monitoringType!);

  const getAlphabeticallyFirstToken = useCallback(
    (tokenList: TokenWithMonitoring[]) => {
      if (!tokenList || tokenList.length === 0) return null;
      return tokenList
        .slice()
        .sort((a, b) => a.token.localeCompare(b.token))[0];
    },
    [],
  );

  const effectiveSelectedToken = useMemo(() => {
    if (selectedToken) {
      return selectedToken;
    }

    if (
      showTokenDropdown &&
      monitoringType &&
      availableTokens &&
      availableTokens.length > 0 &&
      !tokensLoading
    ) {
      const firstToken = getAlphabeticallyFirstToken(availableTokens);
      return firstToken;
    }

    return null;
  }, [
    selectedToken,
    availableTokens,
    tokensLoading,
    showTokenDropdown,
    monitoringType,
    getAlphabeticallyFirstToken,
  ]);

  const { isLaunchDisabled, effectiveToken } = useTokenValidation({
    apiToken: apiToken ?? "",
    selectedToken: effectiveSelectedToken,
    showTokenDropdown,
    additionalValidation,
  });

  const handleTokenSelect = useCallback((token: TokenWithMonitoring) => {
    setSelectedToken(token);
  }, []);

  const trackUsage = useCallback(
    (resultsCount: number) => {
      const tokenUsed = effectiveSelectedToken?.token || apiToken;
      if (tokenUsed && resultsCount > 0) {
        trackTokenUsage(tokenUsed, resultsCount);
      }
    },
    [effectiveSelectedToken?.token, apiToken, trackTokenUsage],
  );

  return {
    selectedToken: effectiveSelectedToken,
    effectiveToken,
    isLaunchDisabled,
    handleTokenSelect,
    trackUsage,
    availableTokens,
    tokensLoading,
  };
};
